// src/utils/errorReporter.js

const ERROR_REPORTING_ENDPOINT =
  import.meta.env.VITE_ERROR_REPORTING_ENDPOINT || "/api/report-error"; // Substitua pelo seu endpoint real se necessário

/**
 * Determina o ambiente baseado no hostname.
 * @returns {string} 'development', 'staging', 'production', ou 'unknown'.
 */
const getEnvironment = () => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname.startsWith("127.0.0.1")) {
    return "local";
  }
  // Assumimos que qualquer outra coisa é produção
  return "production";
};

const isNoiseError = (message, stack) => {
  // If user is offline, network failures are expected noise
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }

  const str = `${message || ""} ${stack || ""}`.toLowerCase();
  return (
    str.includes("indexed database") ||
    str.includes("connection to indexed") ||
    str.includes("object store cannot be found") ||
    str.includes("indexeddb") ||
    str.includes("load failed") ||
    str.includes("failed to fetch") ||
    str.includes("signal is aborted") ||
    str.includes("the user aborted a request") ||
    str.includes("script error") ||
    str.includes("is not a valid javascript mime type") ||
    str.includes("not authenticated") ||
    str.includes("invalid or expired token") ||
    str.includes("expired token") ||
    str.includes("provider is disconnected") ||
    str.includes("reading 'style'") ||
    str.includes("window.webkit") ||
    str.includes("messagehandlers") ||
    str.includes("java exception was raised") ||
    str.includes("networkerror") ||
    str.includes("network error") ||
    str.includes("cannot redefine property") ||
    str.includes("internal assertion failed") ||
    str.includes("different project") ||
    str.includes("tried to deserialize key") ||
    str.includes("document is not focused") ||
    str.includes("failed to execute 'writetext'") ||
    str.includes("writetext") ||
    str.includes("requestcredentials") ||
    str.includes("auth/network-request-failed") ||
    str.includes("_logicalformforcontrol") ||
    str.includes("chrome-extension://") ||
    str.includes("moz-extension://") ||
    str.includes("safari-extension://")
  );
};

const getStoredUserId = () => {
  try {
    const rawUser = localStorage.getItem("user") || localStorage.getItem("auth_user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      return parsed.id || parsed.user_id || parsed.email || "";
    }
  } catch (e) {
    // Ignore localStorage parse errors
  }
  return "";
};

/**
 * Envia o relatório de erro para o endpoint do backend.
 * @param {Error|PromiseRejectionEvent} error O objeto de erro ou evento de rejeição de promessa.
 * @param {string} type O tipo de erro (ex: "unhandled_error", "unhandled_rejection").
 * @param {object} context Contexto adicional, como informações do usuário ou da aplicação.
 */
const sendError = async (error, type, context = {}) => {
  const environment = getEnvironment();
  if (environment === "local") {
    console.warn(
      "Error occurred in local environment, ignoring reporting:",
      error,
    );
    return;
  }

  // Extrair mensagem e stack trace
  let message = "Unknown error";
  let stack = "No stack trace available";

  if (error instanceof PromiseRejectionEvent) {
    const reason = error.reason;
    if (reason) {
      message = reason.message || reason.name || String(reason);
      stack = reason.stack || String(reason);
    } else {
      message = "Unhandled Promise Rejection";
    }
  } else if (error instanceof Error) {
    message = error.message || error.name || "Error";
    stack = error.stack || "No stack trace available";
  } else if (typeof error === "string") {
    message = error;
  } else if (error && error.message) {
    message = error.message;
    stack = error.stack || "No stack trace available";
  }

  // Filtrar ruídos conhecidos (desconexões, carteira desconectada, extensões)
  if (isNoiseError(message, stack)) {
    return;
  }

  const userId = context.userId || getStoredUserId();

  try {
    const errorData = {
      message,
      stack,
      type,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      user: userId,
      appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
      ...context,
      environment,
    };

    if (error.filename && error.lineno && error.colno) {
      errorData.filename = error.filename;
      errorData.lineno = error.lineno;
      errorData.colno = error.colno;
    }

    console.log(`Reporting error in ${environment} environment:`, errorData);

    await fetch(ERROR_REPORTING_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(errorData),
      cache: "no-store",
    });
  } catch (reportingError) {
    console.error("Failed to report error:", reportingError);
  }
};

/**
 * Inicializa os listeners globais de erro.
 * Deve ser chamado uma única vez no início da aplicação.
 */
export const initErrorReporter = (context = {}) => {
  // Captura erros síncronos no nível global
  window.onerror = (message, source, lineno, colno, error) => {
    if (!error) {
      error = new Error(message);
      error.stack = `at ${source}:${lineno}:${colno}`;
    }
    sendError(error, "unhandled_error", { ...context, source, lineno, colno });
    return false;
  };

  // Captura rejeições de promessas não tratadas
  window.onunhandledrejection = (event) => {
    event.preventDefault();
    sendError(event, "unhandled_rejection", context);
  };
};

// Exporta a função sendError para ser usada manualmente, se necessário
export const reportManualError = (error, context = {}) => {
  sendError(error, "manual_error", {
    ...context,
    appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
  });
};
